import BusinessIcon from "@mui/icons-material/Business";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CHART_TAB_BUTTON } from "../../../types/chart-dialog.type";
import { useSelector } from "react-redux";
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import React, { useState, useEffect } from "react";
import RotateScreenDialog from '../../shared/RotateScreenDialog';
import useOrientation from '../../../hooks/useOrientation';
import useTutorialTrigger from "../../../hooks/useTutorialTrigger";
import { TUTORIAL_FEATURES } from "../../../config/tutorials.config";

const CommonIconButtons = ({ loading, title: cardTitle, handleOnClickAction, disabled = false, isMobile = false }) => {
    const activeFacilities = useSelector((state) => state.activeFacilities.facilities);
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
    const { showOrientationAlert, handleOrientationAlertClose, checkOrientationBeforeAction, clearPendingAction } = useOrientation(isMobile);
    const { triggerTutorial, trackFeatureUsage } = useTutorialTrigger();
    const tutorialStateRef = React.useRef({
        residentList: false,
        timeGraph: false,
        multipleBuildings: false
    });

    const triggerActionTutorial = React.useCallback((action) => {
        let tutorialKey = null;
        if (action === null) {
            tutorialKey = 'residentList';
        } else if (action === CHART_TAB_BUTTON.CHART) {
            tutorialKey = 'timeGraph';
        } else if (action === CHART_TAB_BUTTON.BUILDING) {
            tutorialKey = 'multipleBuildings';
        }

        const featureMap = {
            residentList: TUTORIAL_FEATURES.RESIDENT_LIST,
            timeGraph: TUTORIAL_FEATURES.TIME_GRAPH,
            multipleBuildings: TUTORIAL_FEATURES.MULTIPLE_BUILDINGS
        };

        if (tutorialKey) {
            trackFeatureUsage(featureMap[tutorialKey]).catch((error) => {
                console.error('Failed to track tutorial usage:', error);
            });
        }

        if (!tutorialKey || tutorialStateRef.current[tutorialKey]) {
            return;
        }

        tutorialStateRef.current[tutorialKey] = true;

        triggerTutorial(featureMap[tutorialKey]).catch((error) => {
            console.error('Failed to trigger tutorial:', error);
        });
    }, [trackFeatureUsage, triggerTutorial]);

    // Listen for custom event to close mobile menu when rotate dialog is closed
    useEffect(() => {
        const handleCloseMobileMenus = () => {
            setMobileMenuAnchor(null);
        };

        window.addEventListener('closeMobileMenus', handleCloseMobileMenus);
        
        return () => {
            window.removeEventListener('closeMobileMenus', handleCloseMobileMenus);
        };
    }, []);

    const buttonStyles = {
        width: 23,
        height: 23,
        mt: 0,
        mr: 0.7,
        borderRadius: "50%", // Makes the button circular
        border: loading ? "none" : "1px solid grey",
        color: loading ? "inherit" : "black",
        ...(loading
            ? {}
            : {
                ":hover": {
                    backgroundColor: "grey",
                    color: "#fff"
                },
                cursor: "pointer",
            }),
    };

    const buttonConfig = [
        { title: "Resident List", icon: <PeopleOutlineIcon sx={{ width: 18, height: 18 }} />, action: null },
        { title: "Chart View", icon: <ShowChartIcon sx={{ width: 18, height: 18 }} />, action: CHART_TAB_BUTTON.CHART },
        ...activeFacilities && activeFacilities.length > 1
            ? [{ title: "Building View", icon: <BusinessIcon sx={{ width: 16, height: 16 }} />, action: CHART_TAB_BUTTON.BUILDING }]
            : []  // If the condition is false, return an empty array to keep the config unchanged
    ];

    // Mobile menu handlers
    const handleMobileMenuOpen = (event) => {
        setMobileMenuAnchor(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMenuAnchor(null);
        // Don't clear pending actions here - let them persist for orientation-based execution
    };

    const handleMobileMenuItemClick = (action) => {
        if (!disabled && handleOnClickAction) {
            if (checkOrientationBeforeAction(action, cardTitle, handleOnClickAction)) {
                triggerActionTutorial(action);
                handleOnClickAction(cardTitle, action);
                clearPendingAction();
            }
        }
        handleMobileMenuClose();
    };

    // Mobile menu items with larger icons
    const getMobileMenuItems = () => {
        return buttonConfig.map(({ title, icon, action }) => ({
            title,
            icon: React.cloneElement(icon, { sx: { fontSize: 20 } }),
            action
        }));
    };

    if (isMobile) {
        return (
            <div>
                <IconButton
                    onClick={handleMobileMenuOpen}
                    sx={{
                        ...buttonStyles,
                        marginRight: 0
                    }}
                    disabled={disabled}
                >
                    <MoreVertIcon sx={{ width: 18, height: 18 }} />
                </IconButton>
                <Menu
                    anchorEl={mobileMenuAnchor}
                    open={Boolean(mobileMenuAnchor)}
                    onClose={handleMobileMenuClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    sx={{
                        zIndex: 9999,
                        '& .MuiPaper-root': {
                            minWidth: '180px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            borderRadius: '8px'
                        }
                    }}
                >
                    {getMobileMenuItems().map((item, index) => (
                        <MenuItem
                            key={index}
                            onClick={() => handleMobileMenuItemClick(item.action)}
                            sx={{
                                py: 1.5,
                                px: 2,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.title}
                                primaryTypographyProps={{
                                    fontSize: '14px',
                                    color: '#333'
                                }}
                            />
                        </MenuItem>
                    ))}
                </Menu>
                <RotateScreenDialog
                    open={showOrientationAlert}
                    onClose={handleOrientationAlertClose}
                />
            </div>
        );
    }

    return (
        <div className={!loading && "cp"}>
            <div>
                {buttonConfig.map(({ title, icon, action }, index) => (
                    <Tooltip key={index} title={title} arrow>
                        <IconButton
                            disableFocusRipple
                            disableRipple
                            sx={{
                                ...buttonStyles,
                                ...(title === "Building View" && { marginRight: 0 }) // Remove marginRight when title is "Building View"
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!disabled && handleOnClickAction) {
                                    triggerActionTutorial(action);
                                    handleOnClickAction(cardTitle, action);
                                }
                            }}
                            className={loading ? "skeleton" : ""}
                            disabled={disabled}
                        >
                            {!loading && icon}
                        </IconButton>
                    </Tooltip>
                ))}
            </div>
        </div>
    );
};

export default CommonIconButtons;
